/*
 * Copyright (C) Ivo Ladage-van Doorn - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Ivo Ladage-van Doorn <ivol37@gmail.com>, 2024
 */

import { InlineImage } from "../../components/InlineImage";
import { scrollToFootnote } from "../../components/Navigation";
import { ReactElement } from "react";

function Voor1300() {
  const sources = [
    {
      name: "Een zevenhonderdjarige 'cope' op koningsgoed: de ontginning van Lindenholt-Neerbosch, Bert Thissen, 2000.",
      source: "Jaarboek Numaga jaargang XLVII - 2000, pagina 53",
    },
    {
      name: "Continuïteit van bewoning te Nijmegen?, Dr H. Brunsting.",
      source: "Tijdschrift Numaga, jaargang II 1995 juli, pagina 59-64",
    },
    {
      name: "De 'Dikke Boom' te Hees, in: Nijmegen XVI, Jonckers, J.H., 1891",
      source: "Regionaal archief Nijmegen",
    },
    {
      name: "Comeback voor Hese Dikke Boom?, M. de Grood, 2000",
      source: "Nijmeegs Katern 14 (2000), nr. 4, p. 44-45",
    },
    {
      name: "De legende van Hees, Paul van der Heijden",
      source: <a href="http://www.noviomagus.nl/Blik/00-05/Blik0005.htm">De Blik</a>,
    },

    {
      name: "Geschiedenis van de Commanderie, G TH Lemmens, 1974",
      source: "Tijdschrift Numaga jaargang XXI 19740-4, pagina 131",
    },
    {
      name: "Stede-atlas van Nijmegen, Dr F Gorissen, 1956",
      source: "Pagina 21",
    },
    {
      name: "Een zevenhonderdjarige 'cope' op koningsgoed: de ontginning van Lindenholt-Neerbosch, Bert Thissen, 2000",
      source: "Tijdschrift Numaga jaargang XLVII, 2000, pagina 56",
    },
    {
      name: "De Zwaanridder en Nijmegen, GHM Claassens, 1991",
      source: "Tijdschrift Numaga jaargang XXXVIII, 1991, pagina 19",
    },
  ];
  const addFootnote = (index: number) => {
    return (
      <sup key={index}>
        <a onClick={() => scrollToFootnote(index)}>{index}</a>
      </sup>
    );
  };

  const addSource = (index: number, name: string, source: string | ReactElement) => {
    return (
      <tr key={index}>
        <td>
          <a id={"footnote-" + index}>{index}</a>
        </td>
        <td className="left">{name}</td>
        <td className="left">{typeof source === "string" ? source : source}</td>
      </tr>
    );
  };

  return (
    <div>
      <div className="box light">
        <div className="row">
          <h1>Neerbosch voor 1300</h1>

          <h3>De ijstijd</h3>
          <InlineImage image="neerbosch/ijstijd.jpg" width={400} subscript="Hoogtebeeld omgeving Nijmegen" />
          <p>
            In de voorlaatste ijstijd (het Saalien, zo'n 200.000 jaar geleden) vormde zich de Nijmeegse stuwwal in het oosten van Nijmegen. Deze brede heuvelrug met een hoogte van
            meer dan 40 meter boven NAP strekt zich vanaf Nijmegen-Oost in een vrij rechte lijn zuidwaarts tot aan Mook uit en vervolgens in oostelijke richting. Ten westen van die
            heuvelrug loopt het af en hebben zich twee "fluvioglaciale vlaktes" gevormd. De eerste loopt tot aan de Graafseweg. Bij de Dennenstraat vanaf de Graafseweg is duidelijk
            de overgang tussen de twee vlaktes te zien door het grote verval (11 meter naar beneden over een afstand van 300 meter). {addFootnote(1)}
          </p>
        </div>
      </div>
      <div className="box main">
        <div className="left">
          <h3>Het Romeinse tijdperk</h3>
          <p>
            De Romeinen stichtten rond 20 voor Christus de eerste legerplaats in het oosten van Nijmegen. Tijdens de Bataafse opstand in 69/70 na Chr. werd de stad verwoest. In het
            noordwesten van Nijmegen bouwden de Romeinen vervolgens een nieuwe stad. Op het Valkhof verrees een legerplaats, waar in tijden van nood ook de bevolking bescherming
            kon vinden. Neerbosch was in de tijd van de Romeinen nog een dichtbebost gebied, er stonden toen waarschijnlijk geen nederzettingen en er liepen geen wegen. In Hees
            echter lag het Heesse veld, de grote begraafplaats van de Romeinen in de 3e eeuw. Er zijn bij opgravingen diverse potten gevonden in Hees uit die tijd.
          </p>
          <p>
            De twee voornaamste Romeinse wegen in die tijd waren de weg naar Grave en de weg naar Cuijk. De weg naar Grave liep parallel aan de huidige Graafseweg; Teersdijk, St
            Teunismolenweg, Oude Graafseweg, Floraweg. Direct ten westen van deze weg lag het Heseveld. De weg naar Cuijk liep langs de huidige Heyendaalseweg; Driehuizerweg,
            Heyendaalseweg, Coehoornstraat, Hertogstraat.
          </p>
          <p>
            In 172 beginnen de eerste Germaanse invallen in het Romeinse rijk. In Zuidwest-Duitsland wordt de versterkte grenswal, de "Limes", in 260 bestormd door Germaanse
            legers, die vanaf dan de bevolking van Gallië en zelfs Spanje terroriseren. Het Romeinse Nijmegen, toen bekend onder de naam Ulpia Noviomagus, heeft die aanval nog
            weten af te houden tot 270. Rond 270 na Chr. verlieten de Romeinen Nijmegen. De Romeinen hebben in die tijd grote moeite om de grenzen van hun rijk te verdedigen.
          </p>
        </div>
        <div className="right">
          <InlineImage image="neerbosch/nijmegen-west-160-ad.jpg" />
          <p>
            Gedurende de gehele 4e eeuw is Nijmegen bewoond door een bevolking die onder de invloed van de Zuidelijke beschaving staat. De Romeinse keizer Constantijn de Grote weet
            tijdelijk een einde te maken aan de chaos aan de grenzen van zijn rijk. Nijmegen wordt heroverd en het Valkhof opnieuw ingericht. Aan het begin van de 5e eeuw bezwijken
            de Romeinse rijksgrenzen onder de Germaanse invallen en verlaten de Romeinen Nijmegen definitief. Rond 450 na Chr. nemen de Franken uit Gallië de macht over.
            {addFootnote(2)}
          </p>
        </div>
      </div>
      <div className="box light">
        <div className="row">
          <h3>De Dikke Boom</h3>
          <InlineImage image="neerbosch/dikke_boom_hees.jpg" width={300} />
          <p>
            Ooit stond er in Hees een reusachtige lindeboom, genaamd de Dikke Boom, op het kruispunt van Tweede Oude Heselaan, Dikkeboomweg, Schependomlaan en Voorstadslaan. Dit
            laatste is al een merkwaardig feit; in zijn hele geschiedenis stond de Dikke Boom altijd op een kruispunt van 4 wegen, met 4 verschillende namen. Op dat moment is Hees
            nog zeer bebost, en wordt dan ook Overbosch genoemd.
          </p>
          <p>
            In het verre verleden zouden er door Germaanse stammen onder deze boom mensenoffers gebracht zijn. Zij aanbaden de bosgod Haesus of Hesus. Dit zou tevens de oorsprong
            kunnen zijn van de naam Hees. Een andere mogelijke oorsprong van de naam Hees is de betekenis van het woord "hees"; "bos van laag hout, struikgewas". Er bestaan bijna
            geen foto's van deze boom, deze ansichtkaart is vermoedelijk de enige overgebleven foto.
            {addFootnote(3)}
            {addFootnote(4)}
          </p>
          <p>
            Op zaterdagavond 21 november 1903 brak in Nijmegen een fel onweer los, dat gepaard ging met storm, hagel en regen. Die avond werd de Dikke Boom getroffen. Aldus de
            Gelderlander: <i>"In Hees kliefde de bliksem den eeuwenoude 'dikken boom', die zoo tal van jaren een heerlijke beschutting bood"</i>.{addFootnote(5)}
          </p>
          <p>
            Maar de herinnering aan de Dikke Boom bleef bestaan. Later werd er een tramhalte naar genoemd, een straat in Hees en zelfs een tweede straat (in Neerbosch). In 1999
            wordt er een stichting opgericht die er voor pleitte om op die plaats een lindeboom terug te plaatsen (Stichting De Dikke Boom), inclusief een bankje net zoals dat er
            vroeger stond. Op 21 november 2003 wordt dat plan verwezenlijkt en vanaf dan prijkt er weer een lindeboom met bankje op de plaats waar deze vroeger ook stond. De
            lindeboom is vooralsnog echter niet zo indrukwekkend als dat de stichting destijds voor ogen had;{" "}
            <i>"Het bestuur heeft zich ten doel gesteld om een zo omvangrijk mogelijke lindeboom te herplaatsen op de hoek van de Voorstadslaan en de Schependomlaan"</i>
          </p>
        </div>
      </div>
      <div className="box main">
        <div className="row">
          <h3>Het Karolingische tijdperk</h3>
          <InlineImage image="neerbosch/karel_de_grote.jpg" width={400} subscript="Karel de Grote geschilderd door Albrecht Dürer" />
          <p>
            Van de 5e tot de 8e eeuw gebeurd er eigenlijk niet zo veel in Nijmegen en/of er is weinig over bekend. Er wonen niet veel mensen en het is geen stad van betekenis. Dat
            veranderd met de opkomst van het Karolingische rijk, in de achtste eeuw wordt Nijmegen opnieuw onderdeel van een groot rijk. Numaga, zoals Nijmegen toen heette, wordt
            zelfs de meest noordelijk gelegen verblijfplaats van Karel de Grote. Op het valkhof laat hij in 770 een palts bouwen (een palts is een bestuurlijke residentie die door
            de keizer af en toe bezocht wordt, op rondreis door zijn rijk. Karel de Grote vertoefde daar in 777, 804, 806 en 808). Buiten deze burcht echter stelt Nijmegen niet
            heel veel voor. De Noormannen nemen de palts rond 880 in. Dit leidt vervolgens tot de vernietiging ervan. Gedurende het Karolingische rijk gebeurd er niets van
            betekenis in Neerbosch en Hees, wat dan nog altijd onontgonnen bossen zijn.
          </p>
        </div>
      </div>
      <div className="box light">
        <div className="row">
          <h3>Het Heilige Roomse Rijk</h3>
          <InlineImage image="neerbosch/heilige_roomse_rijk.webp" width={300} subscript="Vlag van het Heilige Roomse rijk" />
          <p>
            In 962 ontstond uit het oostelijke gedeelte van het Frankische Rijk het Heilige Roomse Rijk. Het was een voortzetting van het oude Romeinse rijk. Frederik I, bijgenaamd
            Barbarossa ("roodbaard") (Waiblingen, 1122 - Anatolië, 10 juni 1190) was een telg van het huis Hohenstaufen, en van 1155 tot aan zijn dood keizer van het Heilige Roomse
            Rijk. Hij liet in 1155 op het valkhof een imposant kasteel bouwen, de Valkhofburcht.
          </p>
          <p>
            Vanaf 1200 ontstonden langs de grote rivieren welvarende handelssteden. Ook Nijmegen ontwikkelde zich van een kleine handelsnederzetting aan de Waaloever tot een
            bloeiende stad. Het middeleeuwse Nijmegen maakte deel uit van het Heilige Roomse Rijk, dat zich zag als de voortzetting van het Romeinse Rijk in West- en Midden-Europa.
            In de dertiende eeuw begon het Heilige Roomse Rijk steeds meer uiteen te vallen in hertogdommen en graafschappen. De invloed van de keizer nam daardoor af. Nijmegen,
            intussen uitgegroeid tot een belangrijk handelscentrum en keizerlijke verblijfplaats, kreeg in 1230 van Rooms-Koning Hendrik VII (de beoogde keizer) dezelfde
            stadsrechten als Aken. De Waalstad werd een vrije rijksstad met een eigen bestuur en eigen rechtspraak, rechtstreeks vallend onder het gezag van de keizer.
          </p>
        </div>
      </div>
      <div className="box main">
        <div className="row">
          <InlineImage image="neerbosch/hees.png" width={400} subscript="De wijk Hees in Nijmegen" />
          <h3>Hees</h3>
          <p>
            Algemeen aangenomen wordt dat Hees reeds in 1196 bestond. Men verwijst daarvoor naar de oorkonde van 20 oktober 1196 van Hendrik VI, keizer van het Heilige Roomse Rijk
            en koning van Sicilië. In die oorkonde bevestigt de keizer de schenking van goederen aan het Nijmeegse pelgrimshuis door de burggraaf Alardus en zijn echtgenote Uda,
            die eerder dit hospitium hadden gesticht. Letterlijk wordt "Hees" echter niet genoemd, er wordt gesproken over een dorp "Degese" waarvan men vermoed dat het om Hees
            gaat:
          </p>
          <p>
            <i>
              ...wettig geschonken hebben van hun eigen bezittingen, en wel met name vier hoeven in het dorp <b>Degese</b>, een hoeve in Ewijk met bijbehorende goederen, een hoeve
              in Weurt, een boerderij in Woezik en een in Lent.
            </i>{" "}
            {addFootnote(6)}
          </p>
          <p>
            Of het hier daadwerkelijk om Hees gaat is onduidelijk. In "Bijdrage voor Vaderlands geschiedenis der oudheidkunde" uit 1847 merkt de schrijver Nijhoff terecht op dat er
            in de oorkonde dan een spelfout zou moeten staan (Dehe<b>s</b>e), wat niet heel waarschijnlijk is. Pas in 1271 wordt Hees voor het eerst echt als dorp of gehucht
            genoemd.
          </p>
        </div>
      </div>
      <div className="box light">
        <div className="row">
          <h3>Gelre</h3>
          <InlineImage image="neerbosch/wapen_van_gelre.png" width={200} subscript="Wapen van Gelre anno 1250" border={false} />

          <p>
            Het ontstaan van Gelre was begonnen in 878 met de nederzetting Gelre, het huidige Geldern in Duitsland, ongeveer tachtig kilometer van Nijmegen. In de elfde eeuw
            ontstond rond deze plaats het graafschap Gelre. Kort nadat Nijmegen onderdeel is geworden van het Heilige Roomse Rijk komt Nijmegen in 1247 in Gelderse handen. Graaf
            Otto II van Gelre en Zutphen krijgt haar als onderpand van de armlastige Rooms-koning Willem II. Willem II, graaf van Holland, kon met dit geld het beleg van het
            opstandige Aken, waar hij zich wilde laten kronen, financieren. Koning Willem heeft zijn pand nooit ingelost en Nijmegen werd en bleef een Gelderse stad. In 1247
            bestaat Gelre dan uit vier delen, te weten de vier kwartieren Nijmegen, Roermond, Zutphen en Arnhem. Het oude Gelre was aanzienlijk groter dan de huidige provincie
            Gelderland. Dit is een belangrijk jaar in de geschiedenis van Hees en Neerbosch, omdat de graven van Gelre beginnen met het ontginnen van dit gebied.
          </p>
          <p>
            Helemaal soepel verliep de verpanding niet. Hoewel op 10 oktober 1247 koning Willem II, zijn vazallen en dienaren opriepen graaf Otto als hun heer te erkennen moest hij
            uiteindelijk naar het zwaard grijpen tegen de Staufen die Willem II trouw bleven. In 1282 probeerde koning Rudolf Nijmegen voor het rijk terug te winnen, maar
            tevergeefs.
            {addFootnote(7)}
          </p>
          <p>
            Vanaf 1250 wordt er begonnen met het verbeteren van de verdediging van Nijmegen, dit recht was verkregen door het krijgen van stadsrechten in 1230. Er wordt een aarden
            wal, palissaden, een gracht en zelfs een stenen wal aangelegd.
          </p>
        </div>
      </div>
      <div className="box main">
        <div className="row">
          <h3>De graven van Gelre</h3>
          <InlineImage image="neerbosch/tombe_otto_2.jpg" width={400} subscript="Graftombe van Otto II bij het klooster Graefanthal" />
          <p>
            Graaf Otto I van Gelre leefde van 1150 tot 1207. Hij nam deel aan de derde Kruistocht (1189 - 1192). Hij was de vader van Gerard IV van Gelre die leefde van 1185 tot
            1229. Gerard IV huwde met Margaretha van Brabant, dochter van hertog Hendrik I van Brabant. Zij kregen onder meer een zoon; Otto II.
          </p>
          <p>
            Graaf Otto II van Gelre leefde van ca 1215 tot 10 januari 1271. Hij stichtte in 1248 het klooster "Graefenthal", op aandringen van zijn vrouw Margaretha van Kleef
            waarmee hij in 1240 huwde. De naam Graefenthal stamt van het Latijnse "vallis comitis" (dal der graven). In 1251 werd het lichaam van Margaretha van Kleef er bijgezet.
            De eerste nonnen (veelal ongehuwde adellijke vrouwen) betrokken in 1250 het klooster. In de tweede helft van de 13e eeuw kwam het klooster tot grote bloei, mede dankzij
            de machtige positie van de graven van Gelre. Het klooster bezat door schenkingen uitgebreide landerijen en boerderijen in de streek. Het graf van Otto II bevindt zich
            nog steeds in het klooster "Graefenthal", ook wel "Gravendaal" genoemd.
          </p>
          <p></p>
          <p>
            In 1255 kreeg Otto II een zoon van Filippa de Dammartin; Reinoud I, later graaf Reinoud I van Gelre. Hij zou later de grond verpachten waaruit Neerbosch ontstond. Tot
            het jaar 1300 was het gebied rondom Neerbosch nog bos. Het gebied was in 3 delen verdeeld; Lyndenholt in het noorden, Honigholt in het oosten en Neder-Teersbroek in het
            westen. Honigholt werd in de late middeleeuwen veel gebruikt om er bijenkorven neer te zetten voor het winnen van honing. Uit die tijden stamt ook de benaming
            'Honigholt' (honingwoud) voor het gebied, een naam waaraan nu nog het Neerbossche verzorgingstehuis Honinghoeve herinnert.
          </p>
          <p>
            Reinoud I had in 1300 al een lange, maar ongelukkige staat van dienst. Door zijn korte eerste huwelijk met de in 1282/83 overleden Irmgard, dochter van hertog Walram IV
            van Limburg, was hij verstrikt geraakt in de strijd om de erfopvolging in het hertogdom Limburg. Op 5 juni 1288, in de slag van Woeringen, leed Reinoud een grote
            nederlaag en geraakte hij persoonlijk in Brabantse gevangenschap. Hij kon zich slechts tegen hoge kosten vrijkopen en zijn financiële positie was mede daardoor zo
            slecht dat hij zich gedwongen zag het graafschap Gelre aan graaf Gwijde van Vlaanderen, de vader van zijn tweede vrouw, te verpanden. Deze verpanding heeft van 1291 tot
            ca. 1297 geduurd. Vanaf 1297 of uiterlijk 1298 regeert Reinoud weer zelf. Nu kenmerkt zijn optreden zich door enorme ambities en zeer grootse plannen, die echter in de
            meeste gevallen niet worden gerealiseerd. Illustratief hiervoor zijn de diverse "Godswaard" (Insula Dei) of "Godsberg" (Mons Dei) gedoopte stichtingen, waarvan wij
            thans amper kunnen bepalen waar ze lagen. Er is wel verondersteld dat de graaf in deze fase van zijn leven aan vlagen van krankzinnigheid is gaan lijden. Hij is in
            ieder geval ziek geweest en in 1318 waarschijnlijk wegens onbekwaamheid door zijn zoon Reinoud II uit de regeringsmacht ontzet en opgesloten in de kerker van de
            Grauwert, een verdedigingstoren van Kasteel Montfort. Reinoud I overlijdt op 9 oktober 1326 te Montfort.
            {addFootnote(8)}
          </p>
        </div>
      </div>
      <div className="box light">
        <div className="row">
          <h3>De Zwaanridder</h3>
          <InlineImage image="neerbosch/zwaanridder.jpg" width={300} subscript="Lohengrin, de zwaanridder, op een postkaart van rond 1900" />
          <p>
            De Zwaanridderlegende is een legende rondom het ontstaan van de eerste kruistocht (1096-1099) en gaat over een figuur "Helias de Zwaanridder" genaamd. Helias is een van
            de zeven kinderen van koning Oriant en koningin Beatrix, die heersen over een niet nader omschreven rijk genaamd Illefort. Door de valse Oriants moeder (Matabrune),
            worden de vijf broers en de zus van Helias in zwanen veranderd. Helias weet ervoor te zorgen dat zijn broers en zus hun menselijke gedaante weer terugkrijgen, maar voor
            een van de broers blijkt dat echter onmogelijk: het zilveren kettinkje dat de metamorfose moet omkeren is omgesmolten tot twee kelken en deze zijn verdwenen. Op bevel
            van een engel vertrekt Helias naar Nijmegen, in een bootje dat voortgesleept wordt door zijn zwanebroer.
          </p>
          <p>
            Het bijzondere aan dit verhaal is dat Nijmegen er zo'n belangrijke rol in speelt, en nog interessanter Graaf Otto I van Gelre. Volgens Claassens {addFootnote(9)} was de
            opdrachtgever van het schrijven van de Zwaanridderlegende vermoedelijk Hendrik I van Brabant. Hendrik I is rond 1200 in een machtsstrijd verwikkeld met de Graaf Otto I
            van Gelre. Als Hendrik I die strijd wint, dwingt hij Otto I tot het tekenen van een verbond. Het verbond tussen Brabant en Gelre wordt in 1203 bezegeld door een
            huwelijk van Gerard IV (1207-1229), zoon van Otto I, met Margaretha, een dochter van Hendrik I. Dit verklaart het voorkomen van Otto I in de legende van de Zwaanridder.
          </p>
        </div>
      </div>
      <div className="box main">
        <div className="row">
          <div className="table-container">
            <table className="left">
              <thead>
                <tr>
                  <th>Nr</th>
                  <th>Naam</th>
                  <th>Bron</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((source, index) => {
                  return addSource(index + 1, source.name, source.source);
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Voor1300;
